/**
 * Decoration Drawing Helpers
 *
 * Static helper methods for drawing platform decorations.
 * All 55+ drawing methods extracted from PlatformDecorator.ts
 *
 * CONVERSION PATTERN:
 * - Changed from instance methods to static methods
 * - Added `random: SeededRandom` parameter to all methods
 * - Methods that create animations return AnimatedDecoration data
 * - All methods are public static for easy calling from zone decorators
 *
 * NOTE: This file demonstrates the refactoring pattern with Zone 1 methods.
 * The remaining zones (2-6) follow the same pattern and should be extracted.
 * Total: ~1,600 lines of drawing code across all zones.
 */

import Phaser from 'phaser';
import { SeededRandom } from '@/utils/SeededRandom';
import { AnimatedDecoration } from './PlatformDecoratorBase';

export class DecorationDrawingHelpers {
  public static drawTinyFlower(
    graphics: Phaser.GameObjects.Graphics,
    random: SeededRandom,
    width: number,
    height: number
  ): AnimatedDecoration | null {
    const x = random.nextFloat(-width * 0.3, width * 0.3);
    const y = -height / 2 - 10; // Above platform surface

    // Flower colors (bright pink, vibrant yellow, pure white)
    const flowerColors = [0xff69b4, 0xffff00, 0xffffff];
    const petalColor = random.pick(flowerColors);
    const centerColor = 0xffd700;

    // Draw 5 petals (bigger: 6px radius, 5px petal size)
    graphics.fillStyle(petalColor, 1);
    for (let i = 0; i < 5; i++) {
      const angle = (i / 5) * Math.PI * 2;
      const petalX = x + Math.cos(angle) * 6;
      const petalY = y + Math.sin(angle) * 6;
      graphics.fillCircle(petalX, petalY, 5);
    }

    // Draw center (bigger)
    graphics.fillStyle(centerColor, 1);
    graphics.fillCircle(x, y, 4);

    // Add white highlight on center
    graphics.fillStyle(0xffffff, 0.8);
    graphics.fillCircle(x - 1, y - 1, 1.5);

    return null;
  }

  /**
   * Draw small frog (pixel art style)
   * Updated: 2x bigger, brighter colors, more animation
   */
  public static drawSmallFrog(
    graphics: Phaser.GameObjects.Graphics,
    random: SeededRandom,
    width: number,
    height: number
  ): AnimatedDecoration | null {
    const x = random.nextFloat(-width * 0.25, width * 0.25);
    const y = -height / 2 - 8;

    // Frog body (brighter green, 2x size)
    graphics.fillStyle(0x7cfc00, 1); // Lawn green - more vibrant
    graphics.fillRect(x - 8, y, 16, 10);

    // Darker belly
    graphics.fillStyle(0x9acd32, 1);
    graphics.fillRect(x - 6, y + 3, 12, 6);

    // Eyes (white with black pupils, bigger)
    graphics.fillStyle(0xffffff, 1);
    graphics.fillCircle(x - 4, y + 2, 3);
    graphics.fillCircle(x + 4, y + 2, 3);

    graphics.fillStyle(0x000000, 1);
    graphics.fillCircle(x - 4, y + 2, 1.5);
    graphics.fillCircle(x + 4, y + 2, 1.5);

    // Eye highlights (make it look alive)
    graphics.fillStyle(0xffffff, 0.9);
    graphics.fillCircle(x - 3, y + 1, 0.8);
    graphics.fillCircle(x + 5, y + 1, 0.8);

    // Add to animated elements for idle animation
    return {
      graphics,
      type: 'frog-idle',
      animationPhase: random.nextFloat(0, Math.PI * 2),
      animationSpeed: 0.03,
    };
  }

  /**
   * Draw firefly (glowing particle)
   * Updated: 4x bigger with multi-layer glow effect
   */
  public static drawFirefly(
    graphics: Phaser.GameObjects.Graphics,
    random: SeededRandom,
    width: number,
    height: number
  ): AnimatedDecoration | null {
    const x = random.nextFloat(-width * 0.4, width * 0.4);
    const y = random.nextFloat(-height / 2 - 30, -height / 2 - 10);

    // Outer glow (large, very transparent)
    graphics.fillStyle(0xffff99, 0.2);
    graphics.fillCircle(x, y, 12);

    // Middle glow
    graphics.fillStyle(0xffff66, 0.4);
    graphics.fillCircle(x, y, 8);

    // Inner glow
    graphics.fillStyle(0xffff00, 0.7);
    graphics.fillCircle(x, y, 5);

    // Core (bright yellow)
    graphics.fillStyle(0xffffff, 1);
    graphics.fillCircle(x, y, 3);

    // Add to animated elements for orbit animation
    return {
      graphics,
      type: 'firefly-orbit',
      animationPhase: random.nextFloat(0, Math.PI * 2),
      animationSpeed: 0.02,
    };
  }

  /**
   * Draw dewdrop (reflective water drop)
   * Updated: 3x bigger with better reflections
   */
  public static drawDewdrop(
    graphics: Phaser.GameObjects.Graphics,
    random: SeededRandom,
    width: number,
    height: number
  ): AnimatedDecoration | null {
    const x = random.nextFloat(-width * 0.4, width * 0.4);
    const y = -height / 2 + random.nextFloat(-2, 2);

    // Drop shadow (darker blue underneath)
    graphics.fillStyle(0x4682b4, 0.4);
    graphics.fillCircle(x + 0.5, y + 0.5, 5);

    // Drop body (cyan/blue, bigger)
    graphics.fillStyle(0x00bfff, 0.8); // Deep sky blue - more vibrant
    graphics.fillCircle(x, y, 5);

    // Main highlight (white, bigger)
    graphics.fillStyle(0xffffff, 0.9);
    graphics.fillCircle(x - 1.5, y - 1.5, 2.5);

    // Secondary highlight (smaller)
    graphics.fillStyle(0xffffff, 0.6);
    graphics.fillCircle(x + 1, y + 1, 1);

    return null;
  }

  /**
   * Draw fallen leaf (brown/orange)
   * Updated: 3x bigger with more detail
   */
  public static drawFallenLeaf(
    graphics: Phaser.GameObjects.Graphics,
    random: SeededRandom,
    width: number,
    height: number
  ): AnimatedDecoration | null {
    const x = random.nextFloat(-width * 0.3, width * 0.3);
    const y = -height / 2 + 1;

    // Leaf colors (vibrant autumn colors)
    const leafColors = [0xd2691e, 0xff8c00, 0xff4500]; // Chocolate, dark orange, orange-red
    const leafColor = random.pick(leafColors);

    // Leaf shadow
    graphics.fillStyle(0x000000, 0.2);
    graphics.fillEllipse(x + 1, y + 1, 12, 7);

    // Leaf body (bigger ellipse)
    graphics.fillStyle(leafColor, 1);
    graphics.fillEllipse(x, y, 12, 7);

    // Main vein (darker, thicker)
    graphics.lineStyle(1.5, 0x654321, 1);
    graphics.beginPath();
    graphics.moveTo(x - 6, y);
    graphics.lineTo(x + 6, y);
    graphics.strokePath();

    // Side veins
    graphics.lineStyle(0.8, 0x654321, 0.7);
    for (let i = -1; i <= 1; i++) {
      if (i !== 0) {
        graphics.beginPath();
        graphics.moveTo(x + i * 3, y);
        graphics.lineTo(x + i * 3 + 2, y - 2);
        graphics.strokePath();
        graphics.beginPath();
        graphics.moveTo(x + i * 3, y);
        graphics.lineTo(x + i * 3 + 2, y + 2);
        graphics.strokePath();
      }
    }

    return null;
  }

  /**
   * Draw mushroom buddy (tiny mushroom next to main one)
   * Updated: 2x bigger with more spots and detail
   */
  public static drawMushroomBuddy(
    graphics: Phaser.GameObjects.Graphics,
    random: SeededRandom,
    width: number,
    height: number
  ): AnimatedDecoration | null {
    const side = random.next() > 0.5 ? 1 : -1;
    const x = side * (width / 2 + 10);
    const y = -height / 2;

    // Tiny mushroom cap (vibrant colors, bigger)
    const capColors = [0xff6b6b, 0xffff00, 0xff69b4]; // Red, yellow, pink
    const capColor = random.pick(capColors);

    // Cap shadow
    graphics.fillStyle(0x000000, 0.2);
    graphics.fillCircle(x + 1, y - 3, 6);

    // Cap
    graphics.fillStyle(capColor, 1);
    graphics.fillCircle(x, y - 4, 6);

    // Spots on cap (2-3 white spots)
    graphics.fillStyle(0xffffff, 0.9);
    const numSpots = 2 + Math.floor(random.next() * 2);
    for (let i = 0; i < numSpots; i++) {
      const angle = (i / numSpots) * Math.PI * 2;
      const spotX = x + Math.cos(angle) * 3;
      const spotY = y - 4 + Math.sin(angle) * 2;
      graphics.fillCircle(spotX, spotY, 1.5);
    }

    // Stem (bigger)
    graphics.fillStyle(0xf5deb3, 1); // Wheat color - lighter
    graphics.fillRect(x - 2.5, y, 5, 6);

    // Stem shading
    graphics.fillStyle(0xdeb887, 0.5);
    graphics.fillRect(x + 1, y, 1.5, 6);

    return null;
  }

  /**
   * ===== ZONE 1 ADDITIONAL TOP DECORATIONS =====
   */

  /**
   * Draw moss patch
   * Fuzzy green growth on platform surface
   */
  public static drawMossPatch(
    graphics: Phaser.GameObjects.Graphics,
    random: SeededRandom,
    width: number,
    height: number
  ): AnimatedDecoration | null {
    const x = random.nextFloat(-width * 0.3, width * 0.3);
    const y = -height / 2 + 1;
    const patchWidth = random.nextFloat(12, 20);
    const patchHeight = random.nextFloat(4, 7);

    // Fuzzy moss (multiple small circles)
    graphics.fillStyle(0x4d7c4d, 0.8);
    const numCircles = 5 + Math.floor(random.next() * 4);
    for (let i = 0; i < numCircles; i++) {
      const circleX = x + random.nextFloat(-patchWidth / 2, patchWidth / 2);
      const circleY = y + random.nextFloat(-patchHeight / 2, patchHeight / 2);
      const circleSize = random.nextFloat(2, 4);
      graphics.fillCircle(circleX, circleY, circleSize);
    }

    // Darker base
    graphics.fillStyle(0x3d6c3d, 0.9);
    graphics.fillEllipse(x, y, patchWidth, patchHeight);

    return null;
  }

  /**
   * Draw acorn
   * Small brown acorn with cap
   */
  public static drawAcorn(
    graphics: Phaser.GameObjects.Graphics,
    random: SeededRandom,
    width: number,
    height: number
  ): AnimatedDecoration | null {
    const x = random.nextFloat(-width * 0.35, width * 0.35);
    const y = -height / 2 - 3;

    // Acorn body (brown oval)
    graphics.fillStyle(0x8b4513, 1);
    graphics.fillEllipse(x, y + 3, 6, 8);

    // Cap (darker brown)
    graphics.fillStyle(0x654321, 1);
    graphics.fillEllipse(x, y, 7, 4);

    // Highlight
    graphics.fillStyle(0xa0522d, 0.7);
    graphics.fillCircle(x - 1, y + 2, 1.5);

    return null;
  }

  /**
   * Draw small twig
   * Thin brown stick
   */
  public static drawSmallTwig(
    graphics: Phaser.GameObjects.Graphics,
    random: SeededRandom,
    width: number,
    height: number
  ): AnimatedDecoration | null {
    const x = random.nextFloat(-width * 0.3, width * 0.3);
    const y = -height / 2 + 1;
    const twigLength = random.nextFloat(10, 18);
    const angle = random.nextFloat(-Math.PI / 6, Math.PI / 6);

    // Twig
    graphics.lineStyle(2, 0x5a4a3a, 0.9);
    graphics.beginPath();
    graphics.moveTo(x, y);
    graphics.lineTo(x + Math.cos(angle) * twigLength, y + Math.sin(angle) * twigLength);
    graphics.strokePath();

    return null;
  }

  /**
   * Draw grass tuft
   * Small blades of grass
   */
  public static drawGrassTuft(
    graphics: Phaser.GameObjects.Graphics,
    random: SeededRandom,
    width: number,
    height: number
  ): AnimatedDecoration | null {
    const x = random.nextFloat(-width * 0.35, width * 0.35);
    const y = -height / 2;

    // Multiple grass blades
    const numBlades = 3 + Math.floor(random.next() * 3);
    for (let i = 0; i < numBlades; i++) {
      const bladeX = x + (i - numBlades / 2) * 2;
      const bladeHeight = random.nextFloat(8, 14);
      const curve = random.nextFloat(-3, 3);

      graphics.lineStyle(1.5, 0x5a8a4a, 0.8);
      graphics.beginPath();
      graphics.moveTo(bladeX, y);
      graphics.lineTo(bladeX + curve, y - bladeHeight);
      graphics.strokePath();
    }

    return null;
  }

  /**
   * Draw tiny snail
   * Small snail with shell
   */
  public static drawTinySnail(
    graphics: Phaser.GameObjects.Graphics,
    random: SeededRandom,
    width: number,
    height: number
  ): AnimatedDecoration | null {
    const x = random.nextFloat(-width * 0.25, width * 0.25);
    const y = -height / 2 - 4;

    // Shell (spiral)
    graphics.fillStyle(0x8b7355, 1);
    graphics.fillCircle(x + 2, y, 5);

    // Shell spiral
    graphics.lineStyle(1, 0x654321, 1);
    graphics.beginPath();
    graphics.arc(x + 2, y, 2, 0, Math.PI * 1.5);
    graphics.strokePath();

    // Body
    graphics.fillStyle(0xd2b48c, 1);
    graphics.fillEllipse(x - 2, y + 2, 6, 3);

    // Eye stalks
    graphics.lineStyle(0.8, 0xd2b48c, 1);
    graphics.lineBetween(x - 3, y, x - 4, y - 2);
    graphics.lineBetween(x - 1, y, x - 2, y - 2);

    // Eyes
    graphics.fillStyle(0x000000, 1);
    graphics.fillCircle(x - 4, y - 2, 0.5);
    graphics.fillCircle(x - 2, y - 2, 0.5);

    return null;
  }

  /**
   * Draw beetle
   * Small black beetle with shiny back
   */
  public static drawBeetle(
    graphics: Phaser.GameObjects.Graphics,
    random: SeededRandom,
    width: number,
    height: number
  ): AnimatedDecoration | null {
    const x = random.nextFloat(-width * 0.3, width * 0.3);
    const y = -height / 2 - 3;

    // Body (black oval)
    graphics.fillStyle(0x1a1a1a, 1);
    graphics.fillEllipse(x, y, 8, 6);

    // Wing split line
    graphics.lineStyle(0.8, 0x0a0a0a, 1);
    graphics.lineBetween(x, y - 3, x, y + 3);

    // Shiny highlight
    graphics.fillStyle(0x4a4a4a, 0.7);
    graphics.fillCircle(x - 2, y - 1, 2);

    // Head
    graphics.fillStyle(0x1a1a1a, 1);
    graphics.fillCircle(x - 5, y, 3);

    // Antennae
    graphics.lineStyle(0.6, 0x1a1a1a, 1);
    graphics.lineBetween(x - 6, y - 1, x - 8, y - 3);
    graphics.lineBetween(x - 6, y + 1, x - 8, y + 3);

    return null;
  }

  /**
   * Draw spider web (corner)
   * Small decorative web
   */
  public static drawSpiderWeb(
    graphics: Phaser.GameObjects.Graphics,
    random: SeededRandom,
    width: number,
    height: number
  ): AnimatedDecoration | null {
    const side = random.next() > 0.5 ? 1 : -1;
    const x = side * (width / 2 - 5);
    const y = -height / 2 - 5;

    // Radial lines
    graphics.lineStyle(0.8, 0xdddddd, 0.6);
    const numRadials = 5;
    for (let i = 0; i < numRadials; i++) {
      const angle = (i / numRadials) * (Math.PI / 2);
      graphics.beginPath();
      graphics.moveTo(x, y);
      graphics.lineTo(x + Math.cos(angle) * 12 * side, y - Math.sin(angle) * 12);
      graphics.strokePath();
    }

    // Concentric arcs
    for (let r = 4; r <= 12; r += 4) {
      graphics.lineStyle(0.6, 0xdddddd, 0.5);
      graphics.beginPath();
      graphics.arc(x, y, r, -Math.PI / 2, 0, false);
      graphics.strokePath();
    }

    return null;
  }

  /**
   * Draw butterfly
   * Colorful butterfly above platform
   */
  public static drawButterfly(
    graphics: Phaser.GameObjects.Graphics,
    random: SeededRandom,
    width: number,
    height: number
  ): AnimatedDecoration | null {
    const x = random.nextFloat(-width * 0.3, width * 0.3);
    const y = -height / 2 - random.nextFloat(15, 25);

    // Wing colors
    const wingColors = [0xff69b4, 0xffd700, 0x87ceeb, 0xffa500];
    const wingColor = random.pick(wingColors);

    // Wings
    graphics.fillStyle(wingColor, 0.8);
    // Left wings
    graphics.fillEllipse(x - 4, y - 2, 6, 4); // Top
    graphics.fillEllipse(x - 4, y + 2, 5, 4); // Bottom
    // Right wings
    graphics.fillEllipse(x + 4, y - 2, 6, 4); // Top
    graphics.fillEllipse(x + 4, y + 2, 5, 4); // Bottom

    // Wing patterns (spots)
    graphics.fillStyle(0x000000, 0.3);
    graphics.fillCircle(x - 4, y - 2, 1.5);
    graphics.fillCircle(x + 4, y - 2, 1.5);

    // Body
    graphics.fillStyle(0x2a2a2a, 1);
    graphics.fillEllipse(x, y, 2, 6);

    // Antennae
    graphics.lineStyle(0.5, 0x2a2a2a, 1);
    graphics.lineBetween(x - 0.5, y - 3, x - 2, y - 5);
    graphics.lineBetween(x + 0.5, y - 3, x + 2, y - 5);

    // Add to animated elements for wing flutter
    return {
      graphics,
      type: 'butterfly-flutter',
      animationPhase: random.nextFloat(0, Math.PI * 2),
      animationSpeed: 0.15,
    };
  }

  /**
   * ===== ZONE 1 ADDITIONAL UNDERSIDE DECORATIONS =====
   */

  /**
   * Draw spider on web (underside)
   * Spider hanging from web thread
   */
  public static drawSpiderOnWeb(
    graphics: Phaser.GameObjects.Graphics,
    random: SeededRandom,
    width: number,
    height: number
  ): AnimatedDecoration | null {
    const x = random.nextFloat(-width * 0.3, width * 0.3);
    const y = height / 2;
    const threadLength = random.nextFloat(15, 25);

    // Web thread
    graphics.lineStyle(0.8, 0xdddddd, 0.7);
    graphics.lineBetween(x, y, x, y + threadLength);

    // Spider body
    const spiderY = y + threadLength;
    graphics.fillStyle(0x1a1a1a, 1);
    graphics.fillCircle(x, spiderY, 3); // Abdomen
    graphics.fillCircle(x, spiderY - 3, 2); // Head

    // Legs (4 on each side)
    graphics.lineStyle(0.6, 0x1a1a1a, 1);
    for (let i = 0; i < 4; i++) {
      const legY = spiderY - 1 + i * 1.5;
      // Left leg
      graphics.beginPath();
      graphics.moveTo(x - 1.5, legY);
      graphics.lineTo(x - 5, legY + 2);
      graphics.strokePath();
      // Right leg
      graphics.beginPath();
      graphics.moveTo(x + 1.5, legY);
      graphics.lineTo(x + 5, legY + 2);
      graphics.strokePath();
    }

    return null;
  }

  /**
   * Draw caterpillar (underside)
   * Inching caterpillar hanging or crawling
   */
  public static drawCaterpillar(
    graphics: Phaser.GameObjects.Graphics,
    random: SeededRandom,
    width: number,
    height: number
  ): AnimatedDecoration | null {
    const x = random.nextFloat(-width * 0.25, width * 0.25);
    const y = height / 2;

    // Caterpillar colors
    const colors = [0x7cfc00, 0xffff00, 0x00ff7f];
    const color = random.pick(colors);

    // Body segments
    const numSegments = 5;
    for (let i = 0; i < numSegments; i++) {
      const segmentY = y + 5 + i * 4;
      const segmentSize = 3 - i * 0.2;

      graphics.fillStyle(color, 0.9);
      graphics.fillCircle(x, segmentY, segmentSize);

      // Stripes
      if (i % 2 === 0) {
        graphics.fillStyle(0x000000, 0.3);
        graphics.fillCircle(x, segmentY, segmentSize * 0.6);
      }
    }

    // Head
    graphics.fillStyle(color, 1);
    graphics.fillCircle(x, y + 5, 3.5);

    // Eyes
    graphics.fillStyle(0x000000, 1);
    graphics.fillCircle(x - 1, y + 4, 0.6);
    graphics.fillCircle(x + 1, y + 4, 0.6);

    return null;
  }

  /**
   * Draw shelf mushroom (underside)
   * Bracket fungus growing from platform bottom
   */
  public static drawShelfMushroom(
    graphics: Phaser.GameObjects.Graphics,
    random: SeededRandom,
    width: number,
    height: number
  ): AnimatedDecoration | null {
    const side = random.next() > 0.5 ? 1 : -1;
    const x = side * (width / 2 - 8);
    const y = height / 2;

    // Shelf cap (semi-circle)
    graphics.fillStyle(0xd2691e, 0.9);
    graphics.beginPath();
    graphics.arc(x, y, 10, side > 0 ? 0 : Math.PI, side > 0 ? Math.PI : Math.PI * 2);
    graphics.closePath();
    graphics.fillPath();

    // Rings on shelf
    graphics.lineStyle(1, 0x8b4513, 0.7);
    graphics.beginPath();
    graphics.arc(x, y, 7, side > 0 ? 0 : Math.PI, side > 0 ? Math.PI : Math.PI * 2);
    graphics.strokePath();
    graphics.beginPath();
    graphics.arc(x, y, 4, side > 0 ? 0 : Math.PI, side > 0 ? Math.PI : Math.PI * 2);
    graphics.strokePath();

    // Underside (lighter)
    graphics.fillStyle(0xf5deb3, 0.8);
    graphics.fillRect(x - (side > 0 ? 0 : 10), y, 10 * Math.abs(side), 2);

    return null;
  }

  /**
   * ===== UNDERSIDE DECORATIONS =====
   * These hang down from the bottom of platforms
   */

  /**
   * Draw hanging roots (Zone 1)
   * Gnarled roots hanging down with multiple tendrils
   */
  public static drawHangingRoots(
    graphics: Phaser.GameObjects.Graphics,
    random: SeededRandom,
    width: number,
    height: number
  ): AnimatedDecoration | null {
    const x = random.nextFloat(-width * 0.3, width * 0.3);
    const y = height / 2; // Bottom of platform
    const rootLength = random.nextFloat(15, 30);

    // Multiple root tendrils
    const numRoots = 2 + Math.floor(random.next() * 2); // 2-3 roots

    for (let i = 0; i < numRoots; i++) {
      const offsetX = (i - numRoots / 2) * 4;
      const curveAmount = random.nextFloat(-5, 5);

      // Root color (dark brown)
      graphics.lineStyle(2, 0x3a2a1a, 0.8);
      graphics.beginPath();
      graphics.moveTo(x + offsetX, y);

      // Draw curvy root using quadratic curve
      const segments = 3;
      for (let s = 0; s < segments; s++) {
        const progress = (s + 1) / segments;
        const curveX = x + offsetX + Math.sin(progress * Math.PI) * curveAmount;
        const curveY = y + (rootLength / segments) * (s + 1);
        graphics.lineTo(curveX, curveY);
      }

      graphics.strokePath();

      // Add small root hairs
      const numHairs = 2;
      for (let h = 0; h < numHairs; h++) {
        const hairY = y + (rootLength / 3) * (h + 1);
        const hairX = x + offsetX + Math.sin((h / numHairs) * Math.PI) * curveAmount;

        graphics.lineStyle(1, 0x3a2a1a, 0.6);
        graphics.beginPath();
        graphics.moveTo(hairX, hairY);
        graphics.lineTo(hairX + random.nextFloat(-3, 3), hairY + 4);
        graphics.strokePath();
      }
    }

    return null;
  }

  /**
   * Draw hanging branch (Zone 1)
   * Thicker woody branch with small twigs
   */
  public static drawHangingBranch(
    graphics: Phaser.GameObjects.Graphics,
    random: SeededRandom,
    width: number,
    height: number
  ): AnimatedDecoration | null {
    const x = random.nextFloat(-width * 0.25, width * 0.25);
    const y = height / 2; // Bottom of platform
    const branchLength = random.nextFloat(20, 35);

    // Main branch (brown, thicker than roots)
    graphics.fillStyle(0x4a3a2a, 0.9);
    graphics.fillRect(x - 3, y, 6, branchLength * 0.6);

    // Branch tip (slightly thinner)
    graphics.fillRect(x - 2, y + branchLength * 0.6, 4, branchLength * 0.4);

    // Add small twigs coming off the branch
    const numTwigs = 2 + Math.floor(random.next() * 2);
    for (let i = 0; i < numTwigs; i++) {
      const twigY = y + (branchLength / (numTwigs + 1)) * (i + 1);
      const twigSide = i % 2 === 0 ? 1 : -1;
      const twigLength = 6;

      graphics.lineStyle(1.5, 0x4a3a2a, 0.8);
      graphics.beginPath();
      graphics.moveTo(x, twigY);
      graphics.lineTo(x + twigSide * twigLength, twigY + twigLength * 0.5);
      graphics.strokePath();
    }

    // Optional: Add a small leaf at the end
    if (random.next() > 0.5) {
      const leafX = x;
      const leafY = y + branchLength;
      graphics.fillStyle(0x2d4a2d, 0.7);
      graphics.fillEllipse(leafX, leafY + 3, 4, 6);
    }

    return null;
  }

  /**
   * ===== ZONE 2 TOP DECORATIONS =====
   */

  /**
   * Draw small bird
   * Perched bird on platform
   */
  public static drawSmallBird(
    graphics: Phaser.GameObjects.Graphics,
    random: SeededRandom,
    width: number,
    height: number
  ): AnimatedDecoration | null {
    const x = random.nextFloat(-width * 0.3, width * 0.3);
    const y = -height / 2 - 6;

    // Body
    graphics.fillStyle(0x8b4513, 1);
    graphics.fillEllipse(x, y, 8, 6);

    // Head
    graphics.fillCircle(x - 4, y - 2, 4);

    // Wing
    graphics.fillStyle(0x654321, 0.9);
    graphics.fillEllipse(x + 2, y, 6, 4);

    // Beak
    graphics.fillStyle(0xffd700, 1);
    graphics.fillTriangle(x - 7, y - 2, x - 7, y - 1, x - 9, y - 1.5);

    // Eye
    graphics.fillStyle(0x000000, 1);
    graphics.fillCircle(x - 5, y - 3, 0.8);

    return null;
  }

  /**
   * Draw bird nest with eggs
   */
  public static drawBirdNest(
    graphics: Phaser.GameObjects.Graphics,
    random: SeededRandom,
    width: number,
    height: number
  ): AnimatedDecoration | null {
    const x = random.nextFloat(-width * 0.2, width * 0.2);
    const y = -height / 2 - 3;

    // Nest (brown twigs)
    graphics.lineStyle(1.5, 0x654321, 0.8);
    for (let i = 0; i < 8; i++) {
      const angle = (i / 8) * Math.PI * 2;
      graphics.beginPath();
      graphics.moveTo(x, y);
      graphics.lineTo(x + Math.cos(angle) * 10, y + Math.sin(angle) * 5);
      graphics.strokePath();
    }

    // Eggs
    const numEggs = 2 + Math.floor(random.next() * 2);
    for (let i = 0; i < numEggs; i++) {
      const eggX = x + (i - (numEggs - 1) / 2) * 4;
      graphics.fillStyle(0xe6d5b8, 1);
      graphics.fillEllipse(eggX, y + 2, 3.5, 5);

      // Speckles
      graphics.fillStyle(0xa0826d, 0.6);
      graphics.fillCircle(eggX - 0.5, y + 1, 0.5);
      graphics.fillCircle(eggX + 0.5, y + 3, 0.5);
    }

    return null;
  }

  /**
   * Draw berries
   * Cluster of small berries
   */
  public static drawBerries(
    graphics: Phaser.GameObjects.Graphics,
    random: SeededRandom,
    width: number,
    height: number
  ): AnimatedDecoration | null {
    const x = random.nextFloat(-width * 0.35, width * 0.35);
    const y = -height / 2 - 5;

    // Berry colors
    const berryColors = [0xff0000, 0x8b008b, 0x4169e1];
    const color = random.pick(berryColors);

    // Cluster of 3-5 berries
    const numBerries = 3 + Math.floor(random.next() * 3);
    for (let i = 0; i < numBerries; i++) {
      const berryX = x + random.nextFloat(-4, 4);
      const berryY = y + random.nextFloat(-3, 3);

      graphics.fillStyle(color, 0.9);
      graphics.fillCircle(berryX, berryY, 3);

      // Highlight
      graphics.fillStyle(0xffffff, 0.5);
      graphics.fillCircle(berryX - 1, berryY - 1, 1);
    }

    // Stem
    graphics.lineStyle(1, 0x228b22, 0.8);
    graphics.lineBetween(x, y - 5, x, y);

    return null;
  }

  /**
   * Draw moth
   * Night-colored moth
   */
  public static drawMoth(
    graphics: Phaser.GameObjects.Graphics,
    random: SeededRandom,
    width: number,
    height: number
  ): AnimatedDecoration | null {
    const x = random.nextFloat(-width * 0.3, width * 0.3);
    const y = -height / 2 - random.nextFloat(12, 20);

    // Wings (brown/gray)
    graphics.fillStyle(0x8b7355, 0.85);
    graphics.fillEllipse(x - 4, y, 7, 4);
    graphics.fillEllipse(x + 4, y, 7, 4);

    // Wing patterns
    graphics.fillStyle(0x654321, 0.6);
    graphics.fillCircle(x - 4, y, 2);
    graphics.fillCircle(x + 4, y, 2);

    // Body
    graphics.fillStyle(0x5a4a3a, 1);
    graphics.fillRect(x - 1, y - 2, 2, 5);

    // Antennae
    graphics.lineStyle(0.5, 0x5a4a3a, 1);
    graphics.lineBetween(x - 0.5, y - 2, x - 2, y - 4);
    graphics.lineBetween(x + 0.5, y - 2, x + 2, y - 4);

    // Add animation
    return {
      graphics,
      type: 'moth-hover',
      animationPhase: random.nextFloat(0, Math.PI * 2),
      animationSpeed: 0.1,
    };
  }

  /**
   * Draw tree moss
   * Moss growing on platform side
   */
  public static drawTreeMoss(
    graphics: Phaser.GameObjects.Graphics,
    random: SeededRandom,
    width: number,
    height: number
  ): AnimatedDecoration | null {
    const side = random.next() > 0.5 ? 1 : -1;
    const x = side * (width / 2);
    const y = -height / 2 + random.nextFloat(-2, 2);

    // Fuzzy moss patches
    graphics.fillStyle(0x556b2f, 0.7);
    const numPatches = 4;
    for (let i = 0; i < numPatches; i++) {
      const patchX = x;
      const patchY = y + i * 3;
      graphics.fillCircle(patchX, patchY, 3);
    }

    // Lighter highlights
    graphics.fillStyle(0x6b8e23, 0.5);
    for (let i = 0; i < numPatches; i++) {
      const patchX = x + side * -1;
      const patchY = y + i * 3;
      graphics.fillCircle(patchX, patchY, 2);
    }

    return null;
  }

  /**
   * ===== ZONE 2 UNDERSIDE DECORATIONS =====
   */

  /**
   * Draw hanging vine (Zone 2)
   * Thick green vine hanging down
   */
  public static drawHangingVine(
    graphics: Phaser.GameObjects.Graphics,
    random: SeededRandom,
    width: number,
    height: number
  ): AnimatedDecoration | null {
    const x = random.nextFloat(-width * 0.3, width * 0.3);
    const y = height / 2;
    const vineLength = random.nextFloat(25, 45);

    // Thick vine with slight curve
    const curveAmount = random.nextFloat(-8, 8);
    graphics.lineStyle(4, 0x2a4a2a, 0.8);
    graphics.beginPath();
    graphics.moveTo(x, y);

    const segments = 5;
    for (let i = 0; i <= segments; i++) {
      const progress = i / segments;
      const curveX = x + Math.sin(progress * Math.PI) * curveAmount;
      const curveY = y + (vineLength / segments) * i;
      graphics.lineTo(curveX, curveY);
    }
    graphics.strokePath();

    return null;
  }

  /**
   * Draw hanging tendril (Zone 2)
   * Thin curly tendril
   */
  public static drawHangingTendril(
    graphics: Phaser.GameObjects.Graphics,
    random: SeededRandom,
    width: number,
    height: number
  ): AnimatedDecoration | null {
    const x = random.nextFloat(-width * 0.4, width * 0.4);
    const y = height / 2;
    const tendrilLength = random.nextFloat(20, 35);

    graphics.lineStyle(2, 0x3d5a3d, 0.7);
    graphics.beginPath();
    graphics.moveTo(x, y);

    // Curly spiral effect
    const coils = 3;
    const segments = 15;
    for (let i = 0; i <= segments; i++) {
      const progress = i / segments;
      const spiralX = x + Math.sin(progress * Math.PI * coils) * 5;
      const spiralY = y + (tendrilLength / segments) * i;
      graphics.lineTo(spiralX, spiralY);
    }
    graphics.strokePath();

    return null;
  }

  /**
   * Draw hanging leaves (Zone 2)
   * Small leaves hanging from a stem
   */
  public static drawHangingLeaves(
    graphics: Phaser.GameObjects.Graphics,
    random: SeededRandom,
    width: number,
    height: number
  ): AnimatedDecoration | null {
    const x = random.nextFloat(-width * 0.25, width * 0.25);
    const y = height / 2;
    const stemLength = random.nextFloat(15, 25);

    // Stem
    graphics.lineStyle(1.5, 0x2a4a2a, 0.7);
    graphics.lineBetween(x, y, x, y + stemLength);

    // Add 2-3 leaves along stem
    const numLeaves = 2 + Math.floor(random.next() * 2);
    for (let i = 0; i < numLeaves; i++) {
      const leafY = y + (stemLength / (numLeaves + 1)) * (i + 1);
      const leafSide = i % 2 === 0 ? 1 : -1;

      graphics.fillStyle(0x3d6a3d, 0.8);
      graphics.fillEllipse(x + leafSide * 4, leafY, 5, 8);
    }

    return null;
  }

  /**
   * ===== ZONE 3 UNDERSIDE DECORATIONS =====
   */

  /**
   * Draw hanging liana (Zone 3)
   * Long thick jungle liana
   */
  public static drawHangingLiana(
    graphics: Phaser.GameObjects.Graphics,
    random: SeededRandom,
    width: number,
    height: number
  ): AnimatedDecoration | null {
    const x = random.nextFloat(-width * 0.3, width * 0.3);
    const y = height / 2;
    const lianaLength = random.nextFloat(40, 60);

    // Very thick liana
    graphics.lineStyle(5, 0x3a5a2a, 0.85);
    graphics.beginPath();
    graphics.moveTo(x, y);

    const segments = 6;
    const curveAmount = random.nextFloat(-10, 10);
    for (let i = 0; i <= segments; i++) {
      const progress = i / segments;
      const curveX = x + Math.sin(progress * Math.PI * 2) * curveAmount;
      const curveY = y + (lianaLength / segments) * i;
      graphics.lineTo(curveX, curveY);
    }
    graphics.strokePath();

    // Add leaves along liana
    for (let i = 0; i < 4; i++) {
      const leafY = y + (lianaLength / 5) * (i + 1);
      const leafX = x + Math.sin((i / 4) * Math.PI * 2) * curveAmount;

      graphics.fillStyle(0x4a7a4a, 0.7);
      graphics.fillEllipse(leafX + 6, leafY, 6, 10);
    }

    return null;
  }

  /**
   * Draw dense hanging leaves (Zone 3)
   * Cluster of leaves
   */
  public static drawHangingLeavesDense(
    graphics: Phaser.GameObjects.Graphics,
    random: SeededRandom,
    width: number,
    height: number
  ): AnimatedDecoration | null {
    const x = random.nextFloat(-width * 0.2, width * 0.2);
    const y = height / 2;

    // Draw many leaves in a cluster
    const numLeaves = 5 + Math.floor(random.next() * 4);
    for (let i = 0; i < numLeaves; i++) {
      const leafX = x + random.nextFloat(-8, 8);
      const leafY = y + random.nextFloat(5, 25);
      const leafSize = random.nextFloat(6, 10);

      graphics.fillStyle(0x4a7a4a, 0.75);
      graphics.fillEllipse(leafX, leafY, leafSize * 0.7, leafSize);
    }

    return null;
  }

  /**
   * ===== ZONE 3 TOP DECORATIONS =====
   */

  /**
   * Draw cloud puff
   */
  public static drawCloudPuff(
    graphics: Phaser.GameObjects.Graphics,
    random: SeededRandom,
    width: number,
    height: number
  ): AnimatedDecoration | null {
    const x = random.nextFloat(-width * 0.3, width * 0.3);
    const y = -height / 2 - random.nextFloat(10, 20);

    // Cloud made of circles
    graphics.fillStyle(0xffffff, 0.7);
    graphics.fillCircle(x - 6, y, 8);
    graphics.fillCircle(x, y - 3, 10);
    graphics.fillCircle(x + 6, y, 8);
    graphics.fillCircle(x, y + 2, 7);

    return null;
  }

  /**
   * Draw flying bird
   */
  public static drawFlyingBird(
    graphics: Phaser.GameObjects.Graphics,
    random: SeededRandom,
    width: number,
    height: number
  ): AnimatedDecoration | null {
    const x = random.nextFloat(-width * 0.35, width * 0.35);
    const y = -height / 2 - random.nextFloat(15, 30);

    // Simple bird silhouette (V shape wings)
    graphics.lineStyle(2, 0x333333, 0.8);
    graphics.beginPath();
    graphics.moveTo(x - 8, y);
    graphics.lineTo(x, y - 3);
    graphics.lineTo(x + 8, y);
    graphics.strokePath();

    return null;
  }

  /**
   * Draw sky butterfly
   */
  public static drawSkyButterfly(
    graphics: Phaser.GameObjects.Graphics,
    random: SeededRandom,
    width: number,
    height: number
  ): AnimatedDecoration | null {
    const x = random.nextFloat(-width * 0.3, width * 0.3);
    const y = -height / 2 - random.nextFloat(15, 25);

    // Lighter colors for sky zone
    const colors = [0x87ceeb, 0xadd8e6, 0xe0f6ff];
    const color = random.pick(colors);

    graphics.fillStyle(color, 0.85);
    graphics.fillEllipse(x - 4, y, 6, 4);
    graphics.fillEllipse(x + 4, y, 6, 4);

    graphics.fillStyle(0x4682b4, 1);
    graphics.fillEllipse(x, y, 2, 4);

    return null;
  }

  /**
   * Draw floating feather
   */
  public static drawFloatingFeather(
    graphics: Phaser.GameObjects.Graphics,
    random: SeededRandom,
    width: number,
    height: number
  ): AnimatedDecoration | null {
    const x = random.nextFloat(-width * 0.4, width * 0.4);
    const y = -height / 2 - random.nextFloat(12, 25);

    // Feather shaft
    graphics.lineStyle(1, 0xf0e68c, 0.9);
    graphics.lineBetween(x, y - 8, x, y + 8);

    // Feather barbs
    graphics.lineStyle(0.8, 0xf0e68c, 0.7);
    for (let i = -3; i <= 3; i++) {
      const barbY = y + i * 2.5;
      graphics.lineBetween(x, barbY, x - 4, barbY + 1);
      graphics.lineBetween(x, barbY, x + 4, barbY + 1);
    }

    // Animate floating
    return {
      graphics,
      type: 'feather-float',
      animationPhase: random.nextFloat(0, Math.PI * 2),
      animationSpeed: 0.05,
    };
  }

  /**
   * Draw wind swirl
   */
  public static drawWindSwirl(
    graphics: Phaser.GameObjects.Graphics,
    random: SeededRandom,
    width: number,
    height: number
  ): AnimatedDecoration | null {
    const x = random.nextFloat(-width * 0.2, width * 0.2);
    const y = -height / 2 - random.nextFloat(8, 15);

    // Spiral wind effect
    graphics.lineStyle(1.5, 0xe6e6fa, 0.6);
    const numPoints = 8;
    for (let i = 0; i < numPoints; i++) {
      const angle = (i / numPoints) * Math.PI * 2;
      const radius = 3 + i;
      const px = x + Math.cos(angle) * radius;
      const py = y + Math.sin(angle) * radius;

      if (i === 0) {
        graphics.beginPath();
        graphics.moveTo(px, py);
      } else {
        graphics.lineTo(px, py);
      }
    }
    graphics.strokePath();

    return null;
  }

  /**
   * ===== ZONE 4 TOP DECORATIONS =====
   */

  /**
   * Draw twinkling star
   */
  public static drawTwinklingStar(
    graphics: Phaser.GameObjects.Graphics,
    random: SeededRandom,
    width: number,
    height: number
  ): AnimatedDecoration | null {
    const x = random.nextFloat(-width * 0.4, width * 0.4);
    const y = -height / 2 - random.nextFloat(10, 25);

    // Star glow
    graphics.fillStyle(0xffff00, 0.3);
    graphics.fillCircle(x, y, 8);

    // Star points
    graphics.fillStyle(0xffffff, 1);
    const numPoints = 5;
    const outerRadius = 5;
    const innerRadius = 2;

    graphics.beginPath();
    for (let i = 0; i < numPoints * 2; i++) {
      const angle = (i / (numPoints * 2)) * Math.PI * 2 - Math.PI / 2;
      const radius = i % 2 === 0 ? outerRadius : innerRadius;
      const px = x + Math.cos(angle) * radius;
      const py = y + Math.sin(angle) * radius;

      if (i === 0) {
        graphics.moveTo(px, py);
      } else {
        graphics.lineTo(px, py);
      }
    }
    graphics.closePath();
    graphics.fillPath();

    // Twinkle animation
    return {
      graphics,
      type: 'star-twinkle',
      animationPhase: random.nextFloat(0, Math.PI * 2),
      animationSpeed: 0.08,
    };
  }

  /**
   * Draw crescent moon
   */
  public static drawCrescentMoon(
    graphics: Phaser.GameObjects.Graphics,
    random: SeededRandom,
    width: number,
    height: number
  ): AnimatedDecoration | null {
    const x = random.nextFloat(-width * 0.15, width * 0.15);
    const y = -height / 2 - 15;

    // Moon circle
    graphics.fillStyle(0xfffacd, 1);
    graphics.fillCircle(x, y, 10);

    // Shadow to create crescent
    graphics.fillStyle(0x2f4f4f, 1);
    graphics.fillCircle(x + 5, y - 2, 9);

    return null;
  }

  /**
   * Draw shooting star
   */
  public static drawShootingStar(
    graphics: Phaser.GameObjects.Graphics,
    random: SeededRandom,
    width: number,
    height: number
  ): AnimatedDecoration | null {
    const x = random.nextFloat(-width * 0.3, width * 0.3);
    const y = -height / 2 - random.nextFloat(12, 22);

    // Star
    graphics.fillStyle(0xffffff, 1);
    graphics.fillCircle(x, y, 3);

    // Trail
    graphics.lineStyle(2, 0xffd700, 0.7);
    graphics.lineBetween(x, y, x - 12, y + 8);

    graphics.lineStyle(1.5, 0xffffff, 0.5);
    graphics.lineBetween(x, y, x - 15, y + 10);

    return null;
  }

  /**
   * Draw star dust
   */
  public static drawStarDust(
    graphics: Phaser.GameObjects.Graphics,
    random: SeededRandom,
    width: number,
    height: number
  ): AnimatedDecoration | null {
    const x = random.nextFloat(-width * 0.35, width * 0.35);
    const y = -height / 2 - random.nextFloat(8, 18);

    // Multiple tiny sparkles
    const numSparkles = 5 + Math.floor(random.next() * 4);
    for (let i = 0; i < numSparkles; i++) {
      const sx = x + random.nextFloat(-8, 8);
      const sy = y + random.nextFloat(-5, 5);
      const size = random.nextFloat(1, 2);

      graphics.fillStyle(0xffffff, random.nextFloat(0.5, 1));
      graphics.fillCircle(sx, sy, size);
    }

    return null;
  }

  /**
   * Draw night moth (glowing)
   */
  public static drawNightMoth(
    graphics: Phaser.GameObjects.Graphics,
    random: SeededRandom,
    width: number,
    height: number
  ): AnimatedDecoration | null {
    const x = random.nextFloat(-width * 0.3, width * 0.3);
    const y = -height / 2 - random.nextFloat(12, 20);

    // Glowing wings
    graphics.fillStyle(0x9370db, 0.7);
    graphics.fillEllipse(x - 4, y, 6, 4);
    graphics.fillEllipse(x + 4, y, 6, 4);

    // Wing glow
    graphics.fillStyle(0xdda0dd, 0.4);
    graphics.fillCircle(x - 4, y, 4);
    graphics.fillCircle(x + 4, y, 4);

    // Body
    graphics.fillStyle(0x483d8b, 1);
    graphics.fillRect(x - 1, y - 2, 2, 4);

    return null;
  }

  /**
   * ===== ZONE 5 TOP DECORATIONS =====
   */

  /**
   * Draw gold coin
   */
  public static drawGoldCoin(
    graphics: Phaser.GameObjects.Graphics,
    random: SeededRandom,
    width: number,
    height: number
  ): AnimatedDecoration | null {
    const x = random.nextFloat(-width * 0.35, width * 0.35);
    const y = -height / 2 - 8;

    // Coin body
    graphics.fillStyle(0xffd700, 1);
    graphics.fillCircle(x, y, 6);

    // Inner circle
    graphics.lineStyle(1, 0xb8860b, 1);
    graphics.strokeCircle(x, y, 4);

    // Shine
    graphics.fillStyle(0xffff00, 0.7);
    graphics.fillCircle(x - 2, y - 2, 2);

    return null;
  }

  /**
   * Draw gem crystal
   */
  public static drawGemCrystal(
    graphics: Phaser.GameObjects.Graphics,
    random: SeededRandom,
    width: number,
    height: number
  ): AnimatedDecoration | null {
    const x = random.nextFloat(-width * 0.3, width * 0.3);
    const y = -height / 2 - 6;

    const gemColors = [0xff1493, 0x00ced1, 0x9370db, 0x32cd32];
    const color = random.pick(gemColors);

    // Crystal shape (diamond)
    graphics.fillStyle(color, 0.9);
    graphics.beginPath();
    graphics.moveTo(x, y - 6);
    graphics.lineTo(x - 4, y);
    graphics.lineTo(x, y + 6);
    graphics.lineTo(x + 4, y);
    graphics.closePath();
    graphics.fillPath();

    // Facets
    graphics.lineStyle(1, 0xffffff, 0.5);
    graphics.lineBetween(x, y - 6, x, y + 6);
    graphics.lineBetween(x - 4, y, x + 4, y);

    return null;
  }

  /**
   * Draw golden sparkle
   */
  public static drawGoldenSparkle(
    graphics: Phaser.GameObjects.Graphics,
    random: SeededRandom,
    width: number,
    height: number
  ): AnimatedDecoration | null {
    const x = random.nextFloat(-width * 0.4, width * 0.4);
    const y = -height / 2 - random.nextFloat(8, 18);

    // Cross sparkle
    graphics.fillStyle(0xffd700, 1);
    graphics.fillRect(x - 4, y - 1, 8, 2);
    graphics.fillRect(x - 1, y - 4, 2, 8);

    // Center glow
    graphics.fillStyle(0xffff00, 0.8);
    graphics.fillCircle(x, y, 3);

    // Animate
    return {
      graphics,
      type: 'sparkle-pulse',
      animationPhase: random.nextFloat(0, Math.PI * 2),
      animationSpeed: 0.12,
    };
  }

  /**
   * Draw trophy piece
   */
  public static drawTrophyPiece(
    graphics: Phaser.GameObjects.Graphics,
    random: SeededRandom,
    width: number,
    height: number
  ): AnimatedDecoration | null {
    const x = random.nextFloat(-width * 0.2, width * 0.2);
    const y = -height / 2 - 8;

    // Cup
    graphics.fillStyle(0xffd700, 1);
    graphics.fillRect(x - 5, y, 10, 8);
    graphics.fillRect(x - 3, y + 8, 6, 3);

    // Handles
    graphics.lineStyle(2, 0xffd700, 1);
    graphics.arc(x - 5, y + 4, 3, -Math.PI / 2, Math.PI / 2, true);
    graphics.strokePath();
    graphics.arc(x + 5, y + 4, 3, Math.PI / 2, -Math.PI / 2, true);
    graphics.strokePath();

    // Shine
    graphics.fillStyle(0xffffff, 0.6);
    graphics.fillRect(x - 2, y + 2, 2, 4);

    return null;
  }

  /**
   * Draw crown fragment
   */
  public static drawCrownFragment(
    graphics: Phaser.GameObjects.Graphics,
    random: SeededRandom,
    width: number,
    height: number
  ): AnimatedDecoration | null {
    const x = random.nextFloat(-width * 0.15, width * 0.15);
    const y = -height / 2 - 10;

    // Crown base
    graphics.fillStyle(0xffd700, 1);
    graphics.fillRect(x - 8, y + 6, 16, 4);

    // Crown points (3 visible)
    for (let i = 0; i < 3; i++) {
      const pointX = x - 6 + i * 6;
      graphics.beginPath();
      graphics.moveTo(pointX - 2, y + 6);
      graphics.lineTo(pointX, y);
      graphics.lineTo(pointX + 2, y + 6);
      graphics.closePath();
      graphics.fillPath();
    }

    // Jewels
    graphics.fillStyle(0xff0000, 1);
    for (let i = 0; i < 3; i++) {
      const jewelX = x - 6 + i * 6;
      graphics.fillCircle(jewelX, y + 2, 2);
    }

    return null;
  }

  /**
   * ===== ZONE 6 TOP DECORATIONS =====
   */

  /**
   * Draw energy orb
   */
  public static drawEnergyOrb(
    graphics: Phaser.GameObjects.Graphics,
    random: SeededRandom,
    width: number,
    height: number
  ): AnimatedDecoration | null {
    const x = random.nextFloat(-width * 0.3, width * 0.3);
    const y = -height / 2 - random.nextFloat(10, 20);

    // Outer glow
    graphics.fillStyle(0x00ffff, 0.3);
    graphics.fillCircle(x, y, 12);

    // Middle layer
    graphics.fillStyle(0x00bfff, 0.6);
    graphics.fillCircle(x, y, 8);

    // Core
    graphics.fillStyle(0xffffff, 1);
    graphics.fillCircle(x, y, 4);

    // Animate pulse
    return {
      graphics,
      type: 'energy-pulse',
      animationPhase: random.nextFloat(0, Math.PI * 2),
      animationSpeed: 0.1,
    };
  }

  /**
   * Draw rainbow shard
   */
  public static drawRainbowShard(
    graphics: Phaser.GameObjects.Graphics,
    random: SeededRandom,
    width: number,
    height: number
  ): AnimatedDecoration | null {
    const x = random.nextFloat(-width * 0.25, width * 0.25);
    const y = -height / 2 - 8;

    const rainbowColors = [0xff0000, 0xff7f00, 0xffff00, 0x00ff00, 0x0000ff, 0x8b00ff];
    const color = random.pick(rainbowColors);

    // Shard shape
    graphics.fillStyle(color, 0.85);
    graphics.beginPath();
    graphics.moveTo(x, y - 8);
    graphics.lineTo(x - 3, y + 2);
    graphics.lineTo(x, y + 8);
    graphics.lineTo(x + 3, y + 2);
    graphics.closePath();
    graphics.fillPath();

    // Shimmer
    graphics.fillStyle(0xffffff, 0.6);
    graphics.fillRect(x - 1, y - 4, 2, 8);

    return null;
  }

  /**
   * Draw cosmic particle
   */
  public static drawCosmicParticle(
    graphics: Phaser.GameObjects.Graphics,
    random: SeededRandom,
    width: number,
    height: number
  ): AnimatedDecoration | null {
    const x = random.nextFloat(-width * 0.4, width * 0.4);
    const y = -height / 2 - random.nextFloat(8, 20);

    // Swirling particles
    const numParticles = 3 + Math.floor(random.next() * 3);
    for (let i = 0; i < numParticles; i++) {
      const angle = (i / numParticles) * Math.PI * 2;
      const radius = 6;
      const px = x + Math.cos(angle) * radius;
      const py = y + Math.sin(angle) * radius;

      graphics.fillStyle(0xff00ff, 0.7);
      graphics.fillCircle(px, py, 2);

      // Trail
      graphics.fillStyle(0xff00ff, 0.3);
      graphics.fillCircle(px - Math.cos(angle) * 3, py - Math.sin(angle) * 3, 1);
    }

    return null;
  }

  /**
   * Draw portal fragment
   */
  public static drawPortalFragment(
    graphics: Phaser.GameObjects.Graphics,
    random: SeededRandom,
    width: number,
    height: number
  ): AnimatedDecoration | null {
    const x = random.nextFloat(-width * 0.15, width * 0.15);
    const y = -height / 2 - 12;

    // Portal ring segments
    graphics.lineStyle(3, 0x9400d3, 0.8);
    graphics.arc(x, y, 12, 0, Math.PI);
    graphics.strokePath();

    graphics.lineStyle(2, 0xff00ff, 0.6);
    graphics.arc(x, y, 8, Math.PI, Math.PI * 2);
    graphics.strokePath();

    // Energy particles
    for (let i = 0; i < 4; i++) {
      const angle = (i / 4) * Math.PI * 2;
      const px = x + Math.cos(angle) * 10;
      const py = y + Math.sin(angle) * 10;

      graphics.fillStyle(0x00ffff, 0.8);
      graphics.fillCircle(px, py, 2);
    }

    return null;
  }

  /**
   * ===== ZONE 6 UNDERSIDE DECORATIONS =====
   */

  /**
   * Draw lightning bolt
   */
  public static drawLightningBolt(
    graphics: Phaser.GameObjects.Graphics,
    random: SeededRandom,
    width: number,
    height: number
  ): AnimatedDecoration | null {
    const x = random.nextFloat(-width * 0.3, width * 0.3);
    const y = height / 2;
    const length = random.nextFloat(20, 35);

    // Lightning path (zigzag)
    graphics.lineStyle(2, 0xffff00, 0.9);
    graphics.beginPath();
    graphics.moveTo(x, y);

    const segments = 5;
    for (let i = 1; i <= segments; i++) {
      const segmentY = y + (length / segments) * i;
      const segmentX = x + (i % 2 === 0 ? 5 : -5);
      graphics.lineTo(segmentX, segmentY);
    }
    graphics.strokePath();

    // Glow
    graphics.lineStyle(4, 0xffff00, 0.3);
    graphics.beginPath();
    graphics.moveTo(x, y);
    for (let i = 1; i <= segments; i++) {
      const segmentY = y + (length / segments) * i;
      const segmentX = x + (i % 2 === 0 ? 5 : -5);
      graphics.lineTo(segmentX, segmentY);
    }
    graphics.strokePath();

    return null;
  }

  /**
   * Draw energy tendril
   */
  public static drawEnergyTendril(
    graphics: Phaser.GameObjects.Graphics,
    random: SeededRandom,
    width: number,
    height: number
  ): AnimatedDecoration | null {
    const x = random.nextFloat(-width * 0.25, width * 0.25);
    const y = height / 2;
    const length = random.nextFloat(25, 40);

    // Wavy energy tendril
    graphics.lineStyle(3, 0x00ffff, 0.7);
    graphics.beginPath();
    graphics.moveTo(x, y);

    const segments = 10;
    for (let i = 1; i <= segments; i++) {
      const progress = i / segments;
      const waveX = x + Math.sin(progress * Math.PI * 3) * 6;
      const waveY = y + (length / segments) * i;
      graphics.lineTo(waveX, waveY);
    }
    graphics.strokePath();

    // Glow trail
    graphics.lineStyle(5, 0x00ffff, 0.2);
    graphics.beginPath();
    graphics.moveTo(x, y);
    for (let i = 1; i <= segments; i++) {
      const progress = i / segments;
      const waveX = x + Math.sin(progress * Math.PI * 3) * 6;
      const waveY = y + (length / segments) * i;
      graphics.lineTo(waveX, waveY);
    }
    graphics.strokePath();

    // Animate
    return {
      graphics,
      type: 'energy-wave',
      animationPhase: random.nextFloat(0, Math.PI * 2),
      animationSpeed: 0.08,
    };
  }

  /**
   * ===== ZONE 4 UNDERSIDE DECORATIONS =====
   */

  /**
   * Draw icicle (Zone 4)
   * Sharp ice formation
   */
  public static drawIcicle(
    graphics: Phaser.GameObjects.Graphics,
    random: SeededRandom,
    width: number,
    height: number
  ): AnimatedDecoration | null {
    const x = random.nextFloat(-width * 0.4, width * 0.4);
    const y = height / 2;
    const icicleLength = random.nextFloat(20, 40);
    const icicleWidth = random.nextFloat(6, 10);

    // Main icicle body
    graphics.fillStyle(0x88ccff, 0.8);
    graphics.beginPath();
    graphics.moveTo(x, y);
    graphics.lineTo(x - icicleWidth / 2, y + icicleLength * 0.3);
    graphics.lineTo(x, y + icicleLength);
    graphics.lineTo(x + icicleWidth / 2, y + icicleLength * 0.3);
    graphics.closePath();
    graphics.fillPath();

    // Highlight
    graphics.fillStyle(0xffffff, 0.5);
    graphics.fillRect(x - 1, y, 2, icicleLength * 0.6);

    return null;
  }

  /**
   * Draw water drops (Zone 4)
   * Dripping water effect
   */
  public static drawWaterDrops(
    graphics: Phaser.GameObjects.Graphics,
    random: SeededRandom,
    width: number,
    height: number
  ): AnimatedDecoration | null {
    const numDrops = 2 + Math.floor(random.next() * 3);

    for (let i = 0; i < numDrops; i++) {
      const x = random.nextFloat(-width * 0.3, width * 0.3);
      const y = height / 2 + random.nextFloat(10, 25);

      // Water drop
      graphics.fillStyle(0x66aaee, 0.7);
      graphics.fillCircle(x, y, 3);

      // Highlight
      graphics.fillStyle(0xffffff, 0.6);
      graphics.fillCircle(x - 1, y - 1, 1);
    }

    return null;
  }

  /**
   * Draw mini waterfall (Zone 4)
   * Small cascade of water
   */
  public static drawMiniWaterfall(
    graphics: Phaser.GameObjects.Graphics,
    random: SeededRandom,
    width: number,
    height: number
  ): AnimatedDecoration | null {
    const x = random.nextFloat(-width * 0.2, width * 0.2);
    const y = height / 2;
    const fallLength = random.nextFloat(30, 50);

    // Multiple streams
    const numStreams = 3;
    for (let i = 0; i < numStreams; i++) {
      const streamX = x + (i - 1) * 4;

      graphics.lineStyle(2, 0x4488cc, 0.5);
      graphics.beginPath();
      graphics.moveTo(streamX, y);
      graphics.lineTo(streamX + random.nextFloat(-2, 2), y + fallLength);
      graphics.strokePath();
    }

    // Splash at bottom
    graphics.fillStyle(0x66aaee, 0.3);
    graphics.fillCircle(x, y + fallLength, 8);

    return null;
  }

  /**
   * ===== ZONE 5 UNDERSIDE DECORATIONS =====
   */

  /**
   * Draw dripping lava (Zone 5)
   * Molten lava drips
   */
  public static drawDrippingLava(
    graphics: Phaser.GameObjects.Graphics,
    random: SeededRandom,
    width: number,
    height: number
  ): AnimatedDecoration | null {
    const x = random.nextFloat(-width * 0.3, width * 0.3);
    const y = height / 2;
    const dripLength = random.nextFloat(15, 30);

    // Glowing effect
    graphics.fillStyle(0xff8800, 0.3);
    graphics.fillCircle(x, y + dripLength / 2, 12);

    // Lava blob
    graphics.fillStyle(0xff4400, 0.9);
    graphics.fillCircle(x, y + 5, 6);
    graphics.fillCircle(x, y + dripLength, 5);

    // Connecting drip
    graphics.lineStyle(3, 0xff4400, 0.8);
    graphics.lineBetween(x, y + 5, x, y + dripLength);

    // Bright core
    graphics.fillStyle(0xffff00, 0.7);
    graphics.fillCircle(x, y + dripLength, 3);

    // Add to animated elements for pulsing effect
    return {
      graphics,
      type: 'lava-pulse',
      animationPhase: random.nextFloat(0, Math.PI * 2),
      animationSpeed: 0.05,
    };
  }

  /**
   * Draw smoke puffs (Zone 5)
   * Rising smoke from heat
   */
  public static drawSmokePuffs(
    graphics: Phaser.GameObjects.Graphics,
    random: SeededRandom,
    width: number,
    height: number
  ): AnimatedDecoration | null {
    const x = random.nextFloat(-width * 0.25, width * 0.25);
    const y = height / 2;

    // Multiple smoke puffs rising
    const numPuffs = 3;
    for (let i = 0; i < numPuffs; i++) {
      const puffY = y + 10 + i * 12;
      const puffSize = 8 - i * 2;

      graphics.fillStyle(0x4a4a4a, 0.3 - i * 0.08);
      graphics.fillCircle(x + random.nextFloat(-4, 4), puffY, puffSize);
    }

    // Add to animated elements for rising effect
    return {
      graphics,
      type: 'smoke-rise',
      animationPhase: random.nextFloat(0, Math.PI * 2),
      animationSpeed: 0.03,
    };
  }
}
